<template>
  <div class="page-container">
    <v-snackbar v-model="showSnackBar" top right :timeout="3500">
      {{ msg }}
      <template v-slot:action="{ attrs }">
        <v-btn icon small v-bind="attrs" @click="showSnackBar = false">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <PageHeader :title="config.title" :subtitle="config.subtitle" :icon="config.icon" :count="officers.length">
      <template v-slot:actions>
        <v-btn depressed class="btn-act btn-act--neutral" @click="load">
          <v-icon left small>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </template>
    </PageHeader>

    <div class="surface">
      <div class="filter-bar">
        <v-text-field
          v-model="search"
          class="filter-bar-search"
          outlined
          dense
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          :label="'Search ' + config.noun + 's'"
        ></v-text-field>
      </div>

      <div v-if="loading" class="pa-4">
        <v-skeleton-loader type="table-row@5"></v-skeleton-loader>
      </div>
      <div v-else-if="!filtered.length" class="empty-state">
        <v-icon size="48">mdi-account-search-outline</v-icon>
        No {{ config.noun }}s found.
      </div>
      <div v-else class="table-scroll">
        <v-simple-table class="app-table">
          <thead>
            <tr>
              <th>Office</th>
              <th>Officer</th>
              <th>Mobile</th>
              <th>Email</th>
              <th v-if="config.hasCode">Division code</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in filtered" :key="o._id">
              <td data-label="Office"><span class="cell-strong">{{ o.officeName || "—" }}</span></td>
              <td data-label="Officer">{{ o.username || "—" }}</td>
              <td data-label="Mobile">{{ o.mobNumber || "—" }}</td>
              <td data-label="Email"><a v-if="o.emailId" :href="'mailto:' + o.emailId">{{ o.emailId }}</a><span v-else>—</span></td>
              <td v-if="config.hasCode" data-label="Division code">
                <span v-if="o.code && o.code !== 'xxx'" class="app-no">{{ o.code }}</span>
                <span v-else class="cell-muted">Not set</span>
              </td>
              <td class="app-table-actions text-right">
                <div class="row-actions">
                  <v-btn depressed small class="btn-act btn-act--sm btn-act--primary" @click="openEdit(o)">
                    <v-icon left small>mdi-pencil-outline</v-icon>
                    Edit
                  </v-btn>
                </div>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>
    </div>

    <v-dialog v-model="editDialog" max-width="560" persistent>
      <v-card class="dialog-card">
        <div class="dialog-head">
          <div class="dialog-head-title">Edit {{ config.noun }}</div>
          <div class="dialog-head-subtitle">{{ form.officeName }}</div>
        </div>
        <v-form ref="form" class="pa-5">
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field v-model="form.username" outlined dense label="Officer name" :rules="[rules.required]"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="form.mobNumber" outlined dense label="Mobile number" :rules="[rules.required, rules.mobile]"></v-text-field>
            </v-col>
            <v-col cols="12" :sm="config.hasCode ? 6 : 12">
              <v-text-field v-model="form.emailId" outlined dense label="Email" :rules="[rules.required, rules.email]"></v-text-field>
            </v-col>
            <v-col v-if="config.hasCode" cols="12" sm="6">
              <v-text-field v-model="form.code" outlined dense label="Division code" hint="Leave blank to keep the current code" persistent-hint></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.password"
                outlined
                dense
                label="New password"
                hint="Leave blank to keep the current password"
                persistent-hint
                :type="showPassword ? 'text' : 'password'"
                :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append="showPassword = !showPassword"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.confirmPassword"
                outlined
                dense
                label="Confirm password"
                :type="showPassword ? 'text' : 'password'"
                :rules="[(v) => !form.password || v === form.password || 'Passwords do not match']"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-form>
        <v-card-actions class="px-5 pb-5">
          <v-spacer></v-spacer>
          <v-btn depressed class="btn-act btn-act--neutral" @click="editDialog = false">Cancel</v-btn>
          <v-btn depressed class="btn-act btn-act--primary" :disabled="saving" @click="save">
            <v-icon left small>mdi-content-save-outline</v-icon>
            Save changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import CryptoJS from "crypto-js";
import PageHeader from "@/components/Common/PageHeader.vue";

// Must match the key /divisionrange/edit decrypts passwords with.
const PASSWORD_KEY = "film*^@fs(&%eadirgwwvn[!@#$%^&*()shooting";

const KINDS = {
  range: {
    title: "Range Officers",
    subtitle: "Range office accounts and their login details",
    noun: "range officer",
    icon: "mdi-account-hard-hat-outline",
    listUrl: "/range/list",
    hasCode: false,
  },
  division: {
    title: "Division Officers",
    subtitle: "DFO accounts, their login details and division codes",
    noun: "division officer",
    icon: "mdi-account-tie-outline",
    listUrl: "/division/list",
    hasCode: true,
  },
};

// Admin list of range or division officer accounts.
export default {
  name: "OfficerList",
  components: { PageHeader },
  props: {
    kind: { type: String, required: true, validator: (v) => !!KINDS[v] },
  },
  data() {
    return {
      officers: [],
      loading: false,
      saving: false,
      search: "",
      editDialog: false,
      showPassword: false,
      form: {},
      msg: "",
      showSnackBar: false,
      rules: {
        required: (v) => !!(v && String(v).trim()) || "Required",
        mobile: (v) => /^[0-9]{10}$/.test(v || "") || "Enter a 10-digit mobile number",
        email: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || "") || "Enter a valid email",
      },
    };
  },
  computed: {
    config() {
      return KINDS[this.kind];
    },
    filtered() {
      const q = (this.search || "").trim().toLowerCase();
      if (!q) return this.officers;
      return this.officers.filter((o) =>
        [o.officeName, o.username, o.mobNumber, o.emailId, o.code].some((v) => v && String(v).toLowerCase().includes(q))
      );
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    toast(text) {
      this.msg = text;
      this.showSnackBar = true;
    },
    load() {
      this.loading = true;
      axios({ method: "GET", url: this.config.listUrl, headers: { token: localStorage.getItem("token") } })
        .then((response) => {
          this.loading = false;
          this.officers = (response.data && response.data.data) || [];
          if (response.data && response.data.status === false && response.data.msg) this.toast(response.data.msg);
        })
        .catch((err) => {
          // The list endpoints answer 404 when there are no officers.
          this.loading = false;
          this.officers = [];
          if (!err.response || err.response.status !== 404) this.toast("Could not load the list.");
        });
    },
    openEdit(o) {
      this.form = {
        _id: o._id,
        officeName: o.officeName,
        username: o.username,
        mobNumber: o.mobNumber,
        emailId: o.emailId,
        code: o.code && o.code !== "xxx" ? o.code : "",
        password: "",
        confirmPassword: "",
      };
      this.showPassword = false;
      this.editDialog = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
    save() {
      if (!this.$refs.form.validate()) return;
      const f = this.form;
      const payload = {
        _id: f._id,
        username: f.username.trim(),
        mobNumber: f.mobNumber.trim(),
        emailId: f.emailId.trim(),
      };
      if (this.config.hasCode && f.code && f.code.trim()) payload.code = f.code.trim();
      if (f.password) payload.password = CryptoJS.AES.encrypt(f.password, PASSWORD_KEY).toString();
      this.saving = true;
      axios({ method: "PUT", url: "/divisionrange/edit", headers: { token: localStorage.getItem("token") }, data: payload })
        .then((response) => {
          this.saving = false;
          if (response.data && (response.data.success || response.data.status)) {
            this.editDialog = false;
            this.toast("Officer details updated successfully.");
            this.load();
          } else {
            this.toast((response.data && (response.data.message || response.data.msg)) || "Could not update the officer.");
          }
        })
        .catch((err) => {
          this.saving = false;
          const data = err.response && err.response.data;
          this.toast((data && (data.message || data.msg)) || "Could not update the officer.");
        });
    },
  },
};
</script>

<style scoped>
.dialog-card {
  border-radius: 18px !important;
  overflow: hidden;
}
</style>
